import logoBanner from "@assets/images/logoBanner.png";
import CheckboxField from "@features/onboarding/components/CheckboxField";
import RadioButtonField from "@features/onboarding/components/RadioButtonField";
import {
  Button,
  Dialog,
  DialogBackdrop,
  DialogPanel,
  DialogTitle,
  Field,
  Label,
  Menu,
  MenuButton,
  MenuItem,
  MenuItems,
  Switch,
  Tab,
  TabGroup,
  TabList,
  TabPanel,
  TabPanels,
} from "@headlessui/react";
import Container from "@shared/components/Container";
import InputField from "@shared/components/InputField";
import LabelField from "@shared/components/LabelField";
import PasswordLabelField from "@shared/components/PasswordLabelField";
import TextareaField from "@shared/components/TextareaField";
import { Bell, ChevronDown, Heart, MapPin, Plus, Settings, Shield, User, X } from "lucide-react";
import { useState } from "react";

export function HeadlessUIShowcase() {
  const [isMatchModalOpen, setIsMatchModalOpen] = useState(false);
  const [isProfileModalOpen, setIsProfileModalOpen] = useState(false);
  const [notificationsEnabled, setNotificationsEnabled] = useState(true);
  const [privateMode, setPrivateMode] = useState(false);

  // State for gender selection components
  const [gender, setGender] = useState<string>("female");
  const [desiredGenders, setDesiredGenders] = useState<string[]>(["female"]);

  // Handle checkbox change for desired genders
  const handleCheckboxChange = (value: string, checked: boolean) => {
    if (checked) {
      setDesiredGenders([...desiredGenders, value]);
    } else {
      setDesiredGenders(desiredGenders.filter((v) => v !== value));
    }
  };

  // No state needed - these are static visual examples

  return (
    <div className="min-h-screen flex flex-col items-center justify-center p-4 space-y-16 bg-lavender-400">
      {/* Logo Banner */}
      <div className="text-center">
        <img src={logoBanner} alt="Fitties Logo" className="w-full max-w-[380px] mx-auto" />
      </div>

      {/* Disclaimer Banner */}
      <div className="w-full max-w-4xl">
        <div className="bg-yellow-50 border-2 border-yellow-200 rounded-lg p-4">
          <div className="flex items-start space-x-3">
            <div className="flex-shrink-0">
              <span className="text-2xl">⚠️</span>
            </div>
            <div>
              <h3 className="text-lg font-semibold text-yellow-800 mb-2">UI Component Showcase</h3>
              <p className="text-sm text-yellow-700">
                These are <strong>example components</strong> for demonstration purposes only. They show the visual
                design and basic functionality, but are not connected to actual backend services or real data. The
                actual implementation includes proper form validation, error handling, and integration with Document
                Database services.
              </p>
            </div>
          </div>
        </div>
      </div>
      <div className="w-full max-w-6xl space-y-8">
        {/* Architecture Overview */}
        <section className="space-y-6 border-4 border-black bg-gray-100 rounded-xl p-8">
          <div className="text-center">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">✅ Proper Headless UI + Tailwind Architecture</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <h3 className="text-lg font-semibold text-blue-900 mb-2">🧠 Headless UI</h3>
              <p className="text-blue-800">
                Provides accessible behavior, keyboard navigation, focus management, and ARIA attributes
              </p>
            </div>
            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
              <h3 className="text-lg font-semibold text-green-900 mb-2">🎨 Tailwind CSS</h3>
              <p className="text-green-800">
                Handles all visual styling through utility classes - colors, spacing, typography
              </p>
            </div>
            <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
              <h3 className="text-lg font-semibold text-purple-900 mb-2">⚛️ React</h3>
              <p className="text-purple-800">Manages component logic, state, and data flow</p>
            </div>
          </div>
        </section>

        {/* Shared Components Showcase */}
        <section className="space-y-6 border-4 border-black bg-gray-100 rounded-xl p-8">
          <div className="text-center">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Shared Form Components</h2>
            <p className="text-sm text-gray-600 mb-6">
              Reusable form components with consistent styling and error handling
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* InputField Example */}
            <div className="space-y-2">
              <h3 className="text-lg font-semibold text-gray-900">InputField</h3>
              <InputField type="text" name="displayName" placeholder="Enter display name" error="" />
            </div>

            {/* LabelField Example */}
            <div className="space-y-2">
              <h3 className="text-lg font-semibold text-gray-900">LabelField</h3>
              <LabelField label="Email Address" type="email" name="email" error="" />
            </div>

            {/* PasswordLabelField Example */}
            <div className="space-y-2">
              <h3 className="text-lg font-semibold text-gray-900">PasswordLabelField</h3>
              <PasswordLabelField label="Password" name="password" error="" />
            </div>

            {/* TextareaField Example */}
            <div className="space-y-2">
              <h3 className="text-lg font-semibold text-gray-900">TextareaField</h3>
              <TextareaField name="bio" placeholder="Tell us about yourself..." error="" maxLength={150} />
              <div className="text-right text-sm text-gray-500">0/150 characters</div>
            </div>
          </div>

          {/* Container Example */}
          <div className="space-y-2">
            <h3 className="text-lg font-semibold text-gray-900">Container</h3>
            <Container>
              <p className="text-sm text-gray-600">
                This content is wrapped in a Container component with consistent styling.
              </p>
            </Container>
          </div>

          {/* Error States */}
          <div className="space-y-2">
            <h3 className="text-lg font-semibold text-gray-900">Error States</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <InputField
                type="text"
                name="error-input"
                placeholder="Enter display name"
                error="Display name is required"
              />
              <LabelField
                label="Email Address"
                type="email"
                name="error-email"
                error="Please enter a valid email address"
              />
              <PasswordLabelField
                label="Password"
                name="error-password"
                error="Password must be at least 8 characters"
              />
              <TextareaField
                name="error-bio"
                placeholder="Tell us about yourself..."
                error="Bio must be between 10 and 150 characters"
                maxLength={150}
              />
            </div>
          </div>

          <p className="text-sm text-gray-600 text-center mt-4">
            ✅ All components use Headless UI for accessibility and Tailwind for styling
          </p>
        </section>

        {/* Gender Selection Components */}
        <section className="space-y-6 border-4 border-black bg-gray-100 rounded-xl p-8">
          <div className="text-center">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Gender Selection Components</h2>
            <p className="text-sm text-gray-600 mb-6">
              Radio buttons and checkboxes for gender selection in onboarding flows
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {/* RadioButtonField Example */}
            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-gray-900">RadioButtonField</h3>
              <div className="space-y-6">
                <Container>
                  <RadioButtonField
                    name="gender"
                    value="female"
                    label="Female"
                    formValue={gender}
                    onChange={(e) => setGender(e.target.value)}
                  />
                </Container>
                <Container>
                  <RadioButtonField
                    name="gender"
                    value="male"
                    label="Male"
                    formValue={gender}
                    onChange={(e) => setGender(e.target.value)}
                  />
                </Container>
                <Container>
                  <RadioButtonField
                    name="gender"
                    value="nonbinary"
                    label="Other"
                    formValue={gender}
                    onChange={(e) => setGender(e.target.value)}
                  />
                </Container>
              </div>
            </div>

            {/* CheckboxField Example */}
            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-gray-900">CheckboxField</h3>
              <div className="space-y-6">
                <Container>
                  <CheckboxField
                    name="desired_genders"
                    value="female"
                    label="Women"
                    formValue={desiredGenders}
                    onChange={(e) => handleCheckboxChange("female", e.target.checked)}
                  />
                </Container>
                <Container>
                  <CheckboxField
                    name="desired_genders"
                    value="male"
                    label="Men"
                    formValue={desiredGenders}
                    onChange={(e) => handleCheckboxChange("male", e.target.checked)}
                  />
                </Container>
                <Container>
                  <CheckboxField
                    name="desired_genders"
                    value="nonbinary"
                    label="Other"
                    formValue={desiredGenders}
                    onChange={(e) => handleCheckboxChange("nonbinary", e.target.checked)}
                  />
                </Container>
              </div>
            </div>
          </div>

          <p className="text-sm text-gray-600 text-center mt-4">
            ✅ Custom form components with Container styling for gender selection in onboarding
          </p>
        </section>

        {/* Dialog/Modal Examples */}
        <section className="space-y-6 border-4 border-black bg-gray-100 rounded-xl p-8">
          <div className="text-center">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Modal Examples</h2>
          </div>
          <div className="flex gap-4">
            <Button
              onClick={() => setIsProfileModalOpen(true)}
              className="w-full bg-pink-600 font-semibold text-white py-3 px-4 rounded-xl hover:bg-pink-700 focus:outline-none focus:ring-2 focus:ring-pink-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              View Profile
            </Button>
          </div>
        </section>

        {/* Menu/Dropdown Examples */}
        <section className="space-y-6 border-4 border-black bg-gray-100 rounded-xl p-8">
          <div className="text-center">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Profile Actions Menu</h2>

            <Menu as="div" className="relative inline-block text-left">
              <MenuButton
                className="
                flex items-center justify-center gap-2 px-4 py-2
                bg-gray-100 text-gray-900 rounded-lg font-medium
                hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-pink-500 focus:ring-offset-2
                data-[hover]:bg-gray-200 data-[focus]:ring-2 data-[focus]:ring-pink-500
                data-[open]:bg-gray-200
              "
              >
                <Settings className="w-4 h-4" />
                <span className="text-sm font-medium">Profile Settings</span>
                <ChevronDown className="w-4 h-4 data-[open]:rotate-180 transition-transform" />
              </MenuButton>

              <MenuItems
                className="
                absolute right-0 mt-2 w-56 origin-top-right
                bg-white border border-gray-200 rounded-lg shadow-lg ring-1 ring-black ring-opacity-5
                focus:outline-none z-10
                data-[closed]:scale-95 data-[closed]:opacity-0
                data-[enter]:duration-100 data-[leave]:duration-75
                data-[enter]:ease-out data-[leave]:ease-in
              "
              >
                <div className="p-1">
                  <MenuItem>
                    <button
                      type="button"
                      className="
                      flex items-center gap-3 w-full px-3 py-2 text-left
                      text-sm text-gray-900 rounded-md
                      data-[focus]:bg-pink-600 data-[focus]:text-white
                      transition-colors
                    "
                    >
                      <User className="w-4 h-4" />
                      Edit Profile
                    </button>
                  </MenuItem>
                  <MenuItem>
                    <button
                      type="button"
                      className="
                      flex items-center gap-3 w-full px-3 py-2 text-left
                      text-sm text-gray-900 rounded-md
                      data-[focus]:bg-pink-600 data-[focus]:text-white
                      transition-colors
                    "
                    >
                      <Bell className="w-4 h-4" />
                      Notifications
                    </button>
                  </MenuItem>
                  <MenuItem>
                    <button
                      type="button"
                      className="
                      flex items-center gap-3 w-full px-3 py-2 text-left
                      text-sm text-gray-900 rounded-md
                      data-[focus]:bg-pink-600 data-[focus]:text-white
                      transition-colors
                    "
                    >
                      <Shield className="w-4 h-4" />
                      Privacy Settings
                    </button>
                  </MenuItem>
                  <div className="border-t border-gray-200 my-1" />
                  <MenuItem>
                    <button
                      type="button"
                      className="
                      flex items-center gap-3 w-full px-3 py-2 text-left
                      text-sm text-red-600 rounded-md
                      data-[focus]:bg-red-600 data-[focus]:text-white
                      transition-colors
                    "
                    >
                      <X className="w-4 h-4" />
                      Delete Account
                    </button>
                  </MenuItem>
                </div>
              </MenuItems>
            </Menu>

            <p className="text-sm text-gray-600 text-center mt-4">
              ✅ Using Headless UI <code className="bg-gray-200 px-1 rounded">Menu</code> component with Tailwind
              utilities. Includes focus management, keyboard navigation, and click-outside-to-close.
            </p>
          </div>
        </section>

        {/* Switch/Toggle Examples */}
        <section className="space-y-6 border-4 border-black bg-gray-100 rounded-xl p-8">
          <div className="text-center">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">App Settings</h2>
          </div>

          <div className="space-y-4">
            <Field className="flex items-center justify-between">
              <div>
                <Label className="text-sm text-gray-900 font-medium">Push Notifications</Label>
                <p className="text-xs text-gray-600">Get notified about new matches and messages</p>
              </div>
              <Switch
                checked={notificationsEnabled}
                onChange={setNotificationsEnabled}
                className="
                    relative inline-flex h-6 w-11 items-center rounded-full
                    transition-colors focus:outline-none focus:ring-2 focus:ring-pink-500 focus:ring-offset-2
                    data-[checked]:bg-pink-600 data-[unchecked]:bg-gray-200
                    data-[focus]:ring-2 data-[focus]:ring-pink-500
                  "
              >
                <span
                  className="
                    inline-block h-4 w-4 transform rounded-full bg-white transition-transform
                    data-[checked]:translate-x-6 data-[unchecked]:translate-x-1
                  "
                />
              </Switch>
            </Field>

            <Field className="flex items-center justify-between">
              <div>
                <Label className="text-sm text-gray-900 font-medium">Private Mode</Label>
                <p className="text-xs text-gray-600">Hide your profile from discovery</p>
              </div>
              <Switch
                checked={privateMode}
                onChange={setPrivateMode}
                className="
                    relative inline-flex h-6 w-11 items-center rounded-full
                    transition-colors focus:outline-none focus:ring-2 focus:ring-pink-500 focus:ring-offset-2
                    data-[checked]:bg-pink-600 data-[unchecked]:bg-gray-200
                    data-[focus]:ring-2 data-[focus]:ring-pink-500
                  "
              >
                <span
                  className="
                    inline-block h-4 w-4 transform rounded-full bg-white transition-transform
                    data-[checked]:translate-x-6 data-[unchecked]:translate-x-1
                  "
                />
              </Switch>
            </Field>
          </div>

          <p className="text-sm text-gray-600 text-center mt-4">
            ✅ Using Headless UI <code className="bg-gray-200 px-1 rounded">Switch</code> component with Tailwind
            utilities. Includes proper ARIA attributes and keyboard support.
          </p>
        </section>

        {/* Tab Examples */}
        <section className="space-y-6 border-4 border-black bg-gray-100 rounded-xl p-8">
          <div className="text-center">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Navigation Tabs</h2>
          </div>
          <TabGroup>
            <TabList className="flex space-x-1 rounded-lg bg-gray-100 p-1">
              <Tab
                className="
                  w-full rounded-md py-2.5 px-4 text-sm font-medium
                  data-[selected]:bg-white data-[selected]:text-pink-600 data-[selected]:shadow-sm
                  data-[hover]:bg-white/50 data-[focus]:outline-none data-[focus]:ring-2 data-[focus]:ring-pink-500
                  transition-all
                "
              >
                Discover
              </Tab>
              <Tab
                className="
                  w-full rounded-md py-2.5 px-4 text-sm font-medium
                  data-[selected]:bg-white data-[selected]:text-pink-600 data-[selected]:shadow-sm
                  data-[hover]:bg-white/50 data-[focus]:outline-none data-[focus]:ring-2 data-[focus]:ring-pink-500
                  transition-all
                "
              >
                Matches
              </Tab>
              <Tab
                className="
                  w-full rounded-md py-2.5 px-4 text-sm font-medium
                  data-[selected]:bg-white data-[selected]:text-pink-600 data-[selected]:shadow-sm
                  data-[hover]:bg-white/50 data-[focus]:outline-none data-[focus]:ring-2 data-[focus]:ring-pink-500
                  transition-all
                "
              >
                Messages
              </Tab>
            </TabList>
            <TabPanels className="mt-6">
              <TabPanel className="space-y-4">
                <h3 className="text-lg font-semibold text-gray-900">Discover New People</h3>
                <p className="text-sm text-gray-600">
                  Swipe through profiles to find your perfect match. Use filters to narrow down your preferences.
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="bg-gray-50 rounded-lg p-4">
                    <h4 className="text-base font-medium text-gray-900 mb-2">Sarah, 28</h4>
                    <p className="text-xs text-gray-600">Photography enthusiast</p>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-4">
                    <h4 className="text-base font-medium text-gray-900 mb-2">Mike, 31</h4>
                    <p className="text-xs text-gray-600">Adventure seeker</p>
                  </div>
                </div>
              </TabPanel>
              <TabPanel className="space-y-4">
                <h3 className="text-lg font-semibold text-gray-900">Your Matches</h3>
                <p className="text-sm text-gray-600">People who liked you back. Start a conversation!</p>
                <div className="space-y-3">
                  <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                    <div className="w-10 h-10 bg-pink-600 rounded-full flex items-center justify-center">
                      <span className="text-white text-sm font-semibold">J</span>
                    </div>
                    <div className="flex-1">
                      <p className="text-sm text-gray-900 font-medium">Jessica, 26</p>
                      <p className="text-xs text-gray-600">Matched 2 days ago</p>
                    </div>
                  </div>
                </div>
              </TabPanel>
              <TabPanel className="space-y-4">
                <h3 className="text-lg font-semibold text-gray-900">Recent Messages</h3>
                <p className="text-sm text-gray-600">Your ongoing conversations with matches.</p>
                <div className="space-y-3">
                  <div className="border border-gray-200 rounded-lg p-4">
                    <div className="flex justify-end mb-2">
                      <div className="bg-pink-600 text-white rounded-2xl rounded-br-sm px-4 py-2 max-w-xs">
                        <p className="text-sm">Hey there! 👋</p>
                      </div>
                    </div>
                    <div className="flex justify-start">
                      <div className="bg-gray-100 text-gray-900 rounded-2xl rounded-bl-sm px-4 py-2 max-w-xs">
                        <p className="text-sm">Hi! Nice to meet you!</p>
                      </div>
                    </div>
                  </div>
                </div>
              </TabPanel>
            </TabPanels>
          </TabGroup>

          <p className="text-sm text-gray-600 text-center mt-4">
            ✅ Using Headless UI <code className="bg-gray-200 px-1 rounded">TabGroup</code> component with Tailwind
            utilities. Includes keyboard navigation and proper ARIA attributes.
          </p>
        </section>

        {/* Form Examples */}
        <section className="space-y-6 border-4 border-black bg-gray-100 rounded-xl p-8">
          <div className="text-center">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Profile Setup</h2>
          </div>

          <form className="space-y-4">
            <LabelField label="Display Name" type="text" name="displayName" />

            <LabelField label="Email" type="email" name="email" />

            <div className="flex gap-4">
              <Button
                type="submit"
                className="w-full bg-pink-600 font-semibold text-white py-3 px-4 rounded-xl hover:bg-pink-700 focus:outline-none focus:ring-2 focus:ring-pink-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                <Plus className="w-4 h-4 inline mr-2" />
                Save Profile
              </Button>

              <Button
                type="button"
                className="w-full bg-pink-600 font-semibold text-white py-3 px-4 rounded-xl hover:bg-pink-700 focus:outline-none focus:ring-2 focus:ring-pink-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                Cancel
              </Button>
            </div>
          </form>

          <p className="text-sm text-gray-600 text-center mt-4">
            ✅ Using custom <code className="bg-gray-200 px-1 rounded">LabelField</code> component with auth page
            styling
          </p>
        </section>

        {/* Authentication Forms Showcase */}
        <section className="space-y-6 border-4 border-black bg-gray-100 rounded-xl p-8">
          <div className="text-center">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Authentication Forms</h2>
            <p className="text-sm text-gray-600 mb-6">
              Complete authentication flow with validation and error handling
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Sign In Form Example */}
            <div className="space-y-4">
              <h3 className="text-xl font-semibold text-gray-900">Sign In Form</h3>
              <div className="bg-white rounded-lg p-6 border border-gray-200">
                <h4 className="text-lg font-bold text-gray-900 mb-4">Welcome Back</h4>
                <form className="space-y-4">
                  <LabelField label="Email" type="email" name="signin-email" error="" />
                  <PasswordLabelField label="Password" name="signin-password" error="" />
                  <Button className="w-full bg-pink-600 font-semibold text-white py-3 px-4 rounded-xl hover:bg-pink-700 focus:outline-none focus:ring-2 focus:ring-pink-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors">
                    Sign In
                  </Button>
                </form>
                <div className="text-center space-y-2 mt-4">
                  <p className="text-sm text-gray-600">
                    Don't have an account?{" "}
                    <span className="text-pink-600 hover:text-pink-700 font-medium cursor-pointer">Sign Up here</span>
                  </p>
                  <p className="text-sm">
                    <span className="text-pink-600 hover:text-pink-700 font-medium cursor-pointer">
                      Forgot Password?
                    </span>
                  </p>
                </div>
              </div>
            </div>

            {/* Sign Up Form Example */}
            <div className="space-y-4">
              <h3 className="text-xl font-semibold text-gray-900">Sign Up Form</h3>
              <div className="bg-white rounded-lg p-6 border border-gray-200">
                <h4 className="text-lg font-bold text-gray-900 mb-4">Create Account</h4>
                <form className="space-y-4">
                  <LabelField label="Display Name" type="text" name="signup-displayName" error="" />
                  <LabelField label="Email" type="email" name="signup-email" error="" />
                  <PasswordLabelField label="Password" name="signup-password" error="" />
                  <Button className="w-full bg-pink-600 font-semibold text-white py-3 px-4 rounded-xl hover:bg-pink-700 focus:outline-none focus:ring-2 focus:ring-pink-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors">
                    Sign Up
                  </Button>
                </form>
                <div className="text-center mt-4">
                  <p className="text-sm text-gray-600">
                    Already have an account?{" "}
                    <span className="text-pink-600 hover:text-pink-700 font-medium cursor-pointer">Sign In here</span>
                  </p>
                </div>
              </div>
            </div>
          </div>

          <p className="text-sm text-gray-600 text-center mt-4">
            ✅ Complete authentication forms with React Hook Form, Zod validation, and consistent styling
          </p>
        </section>

        {/* Additional Authentication Forms */}
        <section className="space-y-6 border-4 border-black bg-gray-100 rounded-xl p-8">
          <div className="text-center">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Additional Auth Forms</h2>
            <p className="text-sm text-gray-600 mb-6">Password reset and account recovery flows</p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Forgot Password Form Example */}
            <div className="space-y-4">
              <h3 className="text-xl font-semibold text-gray-900">Forgot Password Form</h3>
              <div className="bg-white rounded-lg p-6 border border-gray-200">
                <h4 className="text-lg font-bold text-gray-900 mb-4">Reset Your Password</h4>
                <p className="text-sm text-gray-600 mb-4">
                  Enter your email address and we'll send you a link to reset your password.
                </p>
                <form className="space-y-4">
                  <LabelField label="Email" type="email" name="forgot-email" error="" />
                  <Button className="w-full bg-pink-600 font-semibold text-white py-3 px-4 rounded-xl hover:bg-pink-700 focus:outline-none focus:ring-2 focus:ring-pink-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors">
                    Send Reset Link
                  </Button>
                </form>
                <div className="text-center mt-4">
                  <p className="text-sm text-gray-600">
                    Remember your password?{" "}
                    <span className="text-pink-600 hover:text-pink-700 font-medium cursor-pointer">Sign In here</span>
                  </p>
                </div>
              </div>
            </div>

            {/* Reset Password Form Example */}
            <div className="space-y-4">
              <h3 className="text-xl font-semibold text-gray-900">Reset Password Form</h3>
              <div className="bg-white rounded-lg p-6 border border-gray-200">
                <h4 className="text-lg font-bold text-gray-900 mb-4">Create New Password</h4>
                <p className="text-sm text-gray-600 mb-4">Enter your new password below.</p>
                <form className="space-y-4">
                  <PasswordLabelField label="New Password" name="reset-password" error="" />
                  <PasswordLabelField label="Confirm Password" name="reset-confirm-password" error="" />
                  <Button className="w-full bg-pink-600 font-semibold text-white py-3 px-4 rounded-xl hover:bg-pink-700 focus:outline-none focus:ring-2 focus:ring-pink-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors">
                    Update Password
                  </Button>
                </form>
              </div>
            </div>
          </div>

          <p className="text-sm text-gray-600 text-center mt-4">
            ✅ Complete password recovery flow with email validation and secure password reset
          </p>
        </section>
      </div>

      {/* Match Modal */}
      <Dialog open={isMatchModalOpen} onClose={() => setIsMatchModalOpen(false)} className="relative z-50">
        <DialogBackdrop className="fixed inset-0 bg-black/30 backdrop-blur-sm" />
        <div className="fixed inset-0 flex items-center justify-center p-4">
          <DialogPanel
            className="
            bg-white rounded-lg shadow-xl p-6 w-full max-w-md
            transform transition-all duration-300 ease-out
            data-[closed]:scale-95 data-[closed]:opacity-0
          "
          >
            <div className="text-center">
              <div className="mb-4">
                <Heart className="w-16 h-16 text-pink-600 mx-auto" />
              </div>
              <DialogTitle className="text-xl font-bold text-gray-900 mb-2">It's a Match! 💕</DialogTitle>
              <p className="text-sm text-gray-600 mb-6">You and Sarah liked each other</p>
              <div className="flex gap-3">
                <Button
                  onClick={() => setIsMatchModalOpen(false)}
                  className="flex-1 bg-pink-600 font-semibold text-white py-3 px-4 rounded-xl hover:bg-pink-700 focus:outline-none focus:ring-2 focus:ring-pink-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  Send Message
                </Button>
                <Button
                  onClick={() => setIsMatchModalOpen(false)}
                  className="flex-1 bg-pink-600 font-semibold text-white py-3 px-4 rounded-xl hover:bg-pink-700 focus:outline-none focus:ring-2 focus:ring-pink-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  Keep Swiping
                </Button>
              </div>
            </div>
          </DialogPanel>
        </div>
      </Dialog>

      {/* Profile Modal */}
      <Dialog open={isProfileModalOpen} onClose={() => setIsProfileModalOpen(false)} className="relative z-50">
        <DialogBackdrop className="fixed inset-0 bg-black/30 backdrop-blur-sm" />
        <div className="fixed inset-0 flex items-center justify-center p-4">
          <DialogPanel
            className="
            bg-white rounded-lg shadow-xl p-6 w-full max-w-lg
            transform transition-all duration-300 ease-out
            data-[closed]:scale-95 data-[closed]:opacity-0
          "
          >
            <DialogTitle className="text-lg font-bold text-gray-900 mb-4">Sarah's Profile</DialogTitle>
            <div className="space-y-4">
              <div className="flex items-center gap-4">
                <div className="w-16 h-16 bg-gradient-to-br from-primary to-secondary rounded-2xl flex items-center justify-center">
                  <span className="text-white text-lg font-semibold">S</span>
                </div>
                <div>
                  <h3 className="text-base font-semibold text-gray-900">Sarah, 28</h3>
                  <div className="flex items-center gap-2 text-xs text-gray-600">
                    <MapPin className="w-3 h-3" />
                    <span>2 miles away</span>
                  </div>
                </div>
              </div>
              <p className="text-sm text-gray-900">
                Professional photographer who loves hiking, coffee, and capturing life's beautiful moments. Looking for
                someone who shares my passion for adventure and creativity!
              </p>
              <div className="flex gap-3 pt-4">
                <Button
                  onClick={() => setIsProfileModalOpen(false)}
                  className="flex-1 bg-pink-600 font-semibold text-white py-3 px-4 rounded-xl hover:bg-pink-700 focus:outline-none focus:ring-2 focus:ring-pink-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  <Heart className="w-4 h-4 inline mr-2" />
                  Like
                </Button>
                <Button
                  onClick={() => setIsProfileModalOpen(false)}
                  className="flex-1 bg-pink-600 font-semibold text-white py-3 px-4 rounded-xl hover:bg-pink-700 focus:outline-none focus:ring-2 focus:ring-pink-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  Pass
                </Button>
              </div>
            </div>
          </DialogPanel>
        </div>
      </Dialog>
    </div>
  );
}
